"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseModal = void 0;
const react_1 = __importDefault(require("react"));
const react_draggable_1 = __importDefault(require("react-draggable"));
const Paper_1 = __importDefault(require("@mui/material/Paper"));
const Divider_1 = __importDefault(require("@mui/material/Divider"));
const Dialog_1 = __importDefault(require("@mui/material/Dialog"));
const DialogContent_1 = __importDefault(require("@mui/material/DialogContent"));
const DialogTitle_1 = __importDefault(require("@mui/material/DialogTitle"));
const Backdrop_1 = __importDefault(require("@mui/material/Backdrop"));
const styles_1 = require("@mui/styles");
const style_util_1 = require("../@common/util/style-util");
const _1 = require(".");
const use_tab_index_1 = __importDefault(require("../@common/hooks/app/use-tab-index"));
const use_focus_1 = __importDefault(require("../@common/hooks/app/use-focus"));
const types_1 = require("./types");
const use_keyboard_drag_1 = __importDefault(require("./hooks/use-keyboard-drag"));
const i18n_util_1 = require("../@common/util/i18n-util");
const config_1 = __importDefault(require("../@internal/config"));
const DEFAULT_CONTAINER_STYLES = {
    backgroundColor: 'transparent',
    boxShadow: '0px 0px',
    marginTop: style_util_1.remCalc(-50),
    width: '100%',
    height: '100%',
    display: 'flex',
    flex: 1,
    maxHeight: '100vh',
    maxWidth: '90vw',
    padding: 0,
    margin: 0
};
const DEFAULT_PAPER_STYLES = {
    backgroundColor: 'transparent',
    boxShadow: '0px 0px',
    marginTop: style_util_1.remCalc(-50),
    maxHeight: '100vh',
    maxWidth: '90vw',
    overflow: 'hidden',
    padding: 0,
    margin: 0
};
const PaperDrag = (_a) => {
    var props = __rest(_a, []);
    const [{ enabled, x, y }, enableKeyboardListener] = use_keyboard_drag_1.default();
    return (react_1.default.createElement(react_draggable_1.default, Object.assign({}, (enabled && { position: { x, y } }), { onMouseDown: () => enableKeyboardListener(false), handle: "#draggable-handle" }),
        react_1.default.createElement(Paper_1.default, Object.assign({}, props))));
};
const BaseModal = (_a) => {
    var _b, _c, _d, _e;
    var { open, variant, onClose, getDialogStyles, getContainerStyles, getPaperStyles, options = {}, titleText = '', Title, CloseButton, children, ariaLabel } = _a, dialogProps = __rest(_a, ["open", "variant", "onClose", "getDialogStyles", "getContainerStyles", "getPaperStyles", "options", "titleText", "Title", "CloseButton", "children", "ariaLabel"]);
    const [tabIndex, { setTabIndexOnKeyUp }] = use_tab_index_1.default();
    const [focusRef] = use_focus_1.default();
    const locale = i18n_util_1.getLocale();
    const classes = useStyles();
    const dialogStyles = (_b = getDialogStyles === null || getDialogStyles === void 0 ? void 0 : getDialogStyles({})) !== null && _b !== void 0 ? _b : {};
    const containerStyles = (_c = getContainerStyles === null || getContainerStyles === void 0 ? void 0 : getContainerStyles(DEFAULT_CONTAINER_STYLES)) !== null && _c !== void 0 ? _c : DEFAULT_CONTAINER_STYLES;
    const paperStyles = (_d = getPaperStyles === null || getPaperStyles === void 0 ? void 0 : getPaperStyles(DEFAULT_PAPER_STYLES)) !== null && _d !== void 0 ? _d : DEFAULT_PAPER_STYLES;
    const maybeDraggableProps = options.draggable && {
        PaperComponent: PaperDrag
    };
    const handleOnClose = e => {
        if (options.closeOnBackdropClick) {
            onClose(e, _1.ModalCloseMechanism.BACKDROP_CLICK);
        }
    };
    const handleOnClickClose = e => {
        onClose(e, _1.ModalCloseMechanism.ICON_CLICK);
    };
    const MaybeBackDropComponent = options.Backdrop && {
        BackdropComponent: () => (react_1.default.createElement(Backdrop_1.default, { style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }, open: open }))
    };
    return open ? (react_1.default.createElement(Dialog_1.default, Object.assign({ "aria-labelledby": types_1.ModalAriaLabel.id, onKeyUp: setTabIndexOnKeyUp, tabIndex: 0, open: open, onClose: handleOnClose, style: dialogStyles, PaperProps: Object.assign({ style: paperStyles }, (options.draggable && { tabIndex: -1 })) }, maybeDraggableProps, dialogProps, MaybeBackDropComponent),
        Title && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(Title, { text: titleText, tabIndex: tabIndex, ariaLabel: (_e = `${options.draggable &&
                    config_1.default.i18n.modalKeyboardInstructions[locale]}, ${ariaLabel}`) !== null && _e !== void 0 ? _e : '' }, CloseButton && (react_1.default.createElement(CloseButton, { tabIndex: -1, "aria-label": "close", onClick: handleOnClickClose }))),
            react_1.default.createElement(Divider_1.default, null))),
        !Title && CloseButton && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(DialogTitle_1.default, { className: classes.baseTitleClass },
                react_1.default.createElement(CloseButton, { tabIndex: tabIndex, "aria-label": "close", onClick: handleOnClickClose })),
            react_1.default.createElement(Divider_1.default, null))),
        react_1.default.createElement(DialogContent_1.default, { ref: focusRef, tabIndex: -1, style: containerStyles }, children))) : null;
};
exports.BaseModal = BaseModal;
const useStyles = styles_1.makeStyles(() => ({
    baseTitleClass: {
        padding: '10px 24px',
        backgroundColor: 'white'
    }
}));
exports.default = exports.BaseModal;
